/**
 * ShortBookingRedirect
 * ───────────────────────────────────────────────────────
 * Handles the mini booking URL form `/s/:code`. Resolves the
 * 6-char short code via the public resolve endpoint and
 * navigates to the full `/book/:slug` page. No auth needed.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ShortBookingRedirect() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      try {
        const res = await fetch(`${API}/api/public/booking/resolve-short/${encodeURIComponent(code)}`);
        if (!res.ok) {
          if (cancelled) return;
          setError(res.status === 404 ? 'This booking link is no longer active.' : 'Could not open booking link.');
          return;
        }
        const data = await res.json();
        if (!cancelled && data?.slug) {
          navigate(`/book/${data.slug}`, { replace: true });
        } else if (!cancelled) {
          setError('Could not open booking link.');
        }
      } catch {
        if (!cancelled) setError('Network error — please try again.');
      }
    };
    resolve();
    return () => { cancelled = true; };
  }, [code, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white" data-testid="short-redirect-page">
      {error ? (
        <div className="text-center max-w-sm px-6">
          <div className="h-12 w-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
            <AlertTriangle className="h-5 w-5 text-red-500" />
          </div>
          <p className="text-sm font-semibold text-slate-900">{error}</p>
          <p className="text-xs text-slate-500 mt-1">If you received this link recently, please contact the practice for an updated one.</p>
        </div>
      ) : (
        <div className="text-center">
          <Loader2 className="h-6 w-6 text-slate-400 mx-auto animate-spin" />
          <p className="text-xs text-slate-500 mt-3">Opening booking link…</p>
        </div>
      )}
    </div>
  );
}
